import { useMemo, useEffect, useCallback } from 'react';
import { useApp } from '../../../../context/AppContext';
import useAppContent from '../../../../hooks/useAppContent';
import usePatchCombinations from '../../../../hooks/usePatchCombinations';
import { getPatchClass } from '../utils/patchAssets';

export const useBoardingPassData = () => {
  const { state, actions } = useApp();
  const { appContent } = useAppContent();
  const { getPatchForAnswers, loading: patchLoading } = usePatchCombinations();

  const answers = useMemo(
    () => ({
      q1: state.questionAnswers?.q1 || '',
      q2: state.questionAnswers?.q2 || ''
    }),
    [state.questionAnswers?.q1, state.questionAnswers?.q2]
  );

  const selectedPatch = useMemo(() => {
    if (patchLoading || !answers.q1 || !answers.q2) return null;
    return getPatchForAnswers(answers.q1, answers.q2);
  }, [getPatchForAnswers, answers.q1, answers.q2, patchLoading]);

  const getAnswerDisplay = useCallback(
    (questionId, value) => {
      // Add null check here
      if (!appContent?.acf) return value;

      const optionsMap = {
        q1: appContent.acf.slide_3_question_answers || [],
        q2: appContent.acf.slide_4_question_answers || []
      };

      const options = optionsMap[questionId] || [];
      const matchingOption = options.find(
        (option) => option.value.toLowerCase() === value
      );

      if (matchingOption) {
        return matchingOption.answer;
      }

      const fallbackMap = {
        q1: {
          connection: 'Connection',
          commitment: 'Commitment',
          character: 'Character',
          courage: 'Courage'
        },
        q2: {
          'space-operations': 'Space Operations',
          engineering: 'Engineering',
          'cyber-operations': 'Cyber Operations',
          acquistions: 'Acquisitions',
          intelligence: 'Intelligence'
        }
      };

      return fallbackMap[questionId]?.[value] || value;
    },
    [appContent]
  );

  const boardingPassData = useMemo(() => {
    if (!selectedPatch || !state.userInfo) {
      return {
        backgroundTheme: 'Default',
        backgroundClass: 'space-theme',
        specialty: 'Space Cadet',
        coreValue: 'Exploration',
        patchName: 'Default Patch',
        patchImage: null,
        patchDescription: 'Default patch description',
        patchId: 'default',
        patchClass: 'patch-default',
        date: new Date().toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        }),
        time: new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        })
      };
    }

    const selectedBackground = state.userInfo.selectedBackground;

    return {
      backgroundTheme: selectedBackground?.name || 'Default',
      backgroundClass: selectedBackground?.class || 'default-theme',
      specialty: getAnswerDisplay('q1', answers.q1),
      coreValue: getAnswerDisplay('q2', answers.q2),
      patchName: selectedPatch.name,
      patchImage: selectedPatch.image,
      patchFile: selectedPatch.file,
      patchDescription: selectedPatch.description,
      patchId: selectedPatch.id,
      patchSlug: selectedPatch.slug,
      patchClass: getPatchClass(selectedPatch),
      departingFrom: appContent?.acf?.boarding_pass_departing_from || '',
      arrivingIn: appContent?.acf?.boarding_pass_arriving_in || '',
      date: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  }, [
    selectedPatch,
    state.userInfo,
    answers.q1,
    answers.q2,
    getAnswerDisplay,
    appContent
  ]);

  useEffect(() => {
    if (boardingPassData && !state.boardingPass) {
      actions.setBoardingPass(boardingPassData);
    }
  }, [boardingPassData, state.boardingPass, actions]);

  return {
    boardingPassData,
    selectedPatch,
    patchLoading,
    answers
  };
};
